import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard } from "lucide-react";
import { api, formatApiError, getSid } from "../api";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const COUNTRIES = ["PL", "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR",
  "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PT", "RO", "SK", "SI", "ES", "SE",
  "GB", "US", "CH", "NO", "UA"];

// Checkout: price confirmation -> billing data -> (TEST) payment -> entitlement.
// Parcel/report context (returnTo) is preserved through the whole flow.
export default function CheckoutPage({ kind }) {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const parcelId = params.parcelId ? params.parcelId.replaceAll("~", "/") : null;
  const planCode = params.planCode || null;
  const returnTo = searchParams.get("returnTo") || (parcelId ? `/raport/podglad/${params.parcelId}` : "/panel");

  const [plans, setPlans] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", b2b: false, company: "", nip: "", address: "", country: "PL" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/billing/plans").then((r) => setPlans(r.data.plans)).catch(() => setPlans([]));
  }, []);

  useEffect(() => {
    if (user === false) {
      navigate(`/logowanie?returnTo=${encodeURIComponent(
        kind === "report" ? `/platnosc/raport/${params.parcelId}?returnTo=${encodeURIComponent(returnTo)}`
                          : `/platnosc/plan/${planCode}?returnTo=${encodeURIComponent(returnTo)}`)}`);
    }
    if (user && user.email) setForm((f) => ({ ...f, email: user.email, name: f.name || user.name || "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user) return <div className="p-20 text-center text-slate-500">{t("common.loading")}</div>;

  const plan = kind === "plan" ? (plans || []).find((p) => p.code === planCode) : (plans || []).find((p) => p.code === "report_single");
  if (kind === "plan" && plans && !plan) return <div className="p-20 text-center text-slate-500">404</div>;

  const amount = plan?.price_grosze ?? 0;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const payload = { billing: { ...form, customer_type: form.b2b ? "company" : "private", company: form.company || null, nip: form.nip || null, address: form.address || null }, sid: getSid() };
      const r = kind === "report"
        ? await api.post("/billing/checkout/report", { ...payload, parcel_id: parcelId })
        : await api.post("/billing/checkout/subscription", { ...payload, plan_code: planCode });
      navigate(`/platnosc/test/${r.data.checkout_id}?returnTo=${encodeURIComponent(returnTo)}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSending(false);
  };

  return (
    <div data-testid="checkout-page" className="mx-auto max-w-2xl px-4 py-12 sm:px-6">
      <Helmet><title>{`${t("checkout.title")} — PlanGmina`}</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">{t("checkout.title")}</h1>

      <section data-testid="checkout-summary" className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-5">
        <h2 className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("checkout.summary")}</h2>
        <div className="mt-2 flex items-center justify-between">
          <p className="text-sm font-semibold text-slate-900">
            {kind === "report" ? `${t("checkout.item.report")} — ${parcelId}` : (plan ? (t("lang") === "x" ? "" : plan.name_pl) : "…")}
          </p>
          <p data-testid="checkout-amount" className="font-heading text-xl font-extrabold text-slate-900">
            {(amount / 100).toLocaleString("pl-PL", { minimumFractionDigits: 2 })} zł
          </p>
        </div>
        {kind === "plan" && plan && (
          <p className="mt-1 text-xs text-slate-500">{t(`pricing.interval.${plan.billing_interval}`)}</p>
        )}
        <p className="mt-2 text-[11px] text-slate-400">{t("pricing.tax_note")}</p>
      </section>

      <form onSubmit={submit} className="mt-6 space-y-3">
        <h2 className="text-xs font-bold uppercase tracking-wide text-slate-500">{t("checkout.billing")}</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="bill-name">{t("checkout.name")}</Label>
            <Input id="bill-name" data-testid="checkout-field-name" value={form.name} onChange={set("name")} required minLength={2} className="mt-1" />
          </div>
          <div>
            <Label htmlFor="bill-email">{t("checkout.email")}</Label>
            <Input id="bill-email" data-testid="checkout-field-email" type="email" value={form.email} onChange={set("email")} required className="mt-1" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" data-testid="checkout-field-b2b" checked={form.b2b} onChange={set("b2b")} />
          {t("checkout.b2b")}
        </label>
        {form.b2b && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="bill-company">{t("checkout.company")}</Label>
              <Input id="bill-company" data-testid="checkout-field-company" value={form.company} onChange={set("company")} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="bill-nip">{t("checkout.nip")}</Label>
              <Input id="bill-nip" data-testid="checkout-field-nip" value={form.nip} onChange={set("nip")} className="mt-1" placeholder="1234567890" />
            </div>
          </div>
        )}
        <div>
          <Label htmlFor="bill-address">{t("checkout.address")}</Label>
          <Input id="bill-address" data-testid="checkout-field-address" value={form.address} onChange={set("address")} className="mt-1" />
        </div>
        <div>
          <Label htmlFor="bill-country">{t("checkout.country")}</Label>
          <select id="bill-country" data-testid="checkout-field-country" value={form.country} onChange={set("country")}
                  className="mt-1 flex h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <p data-testid="checkout-test-note" className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800">
          {t("pay.test.banner")}
        </p>
        <Button data-testid="checkout-submit" type="submit" disabled={sending} className="h-11 w-full bg-[#DC143C] text-white hover:bg-[#B91C1C]">
          <CreditCard size={15} className="mr-2" />
          {sending ? t("common.loading") : t("checkout.submit")}
        </Button>
      </form>
    </div>
  );
}
